// @flow

import React, { memo, lazy } from 'react';
import enablePointerEvents from 'app/common/react/components/PinRep/utils/enablePointerEvents';
import SafeSuspense from 'app/packages/safe-suspense';
import { Box } from 'gestalt';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import SourceLink, {
  type SourceLinkStyle,
} from 'app/common/react/components/PinRep/components/ActionBar/SourceLink';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';

const EditButton = lazy(() =>
  import(/* webpackChunkName: "PinRepEditButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/PinRep/components/PinRepEditButton'),
);
const OverflowMenu = lazy(() =>
  import(/* webpackChunkName: "PinRepOverflowMenuButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/PinRep/components/OverflowMenu'),
);
const SendShareLink = lazy(() =>
  import(/* webpackChunkName: "PinRepSendShareLinkButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/PinRep/components/ActionBar/SendShareLink'),
);

export type Props = {|
  actionBarEditBeforeShare: boolean,

  pin: Pin,

  feedbackData: FeedbackData,

  // Indicates why a pin appears in a given grid
  feedbackType: FeedbackType,

  // Used to force a hover experience. See PinRepContainer for more context.
  onToggleFlyout: (showToggle: boolean) => void,
  onToggleModal: (showModal: boolean) => void,
  setForcePrimary: (boolean) => void,

  // The title for the pin (uses more args than just the pin, so determined higher up)
  pinTitle: string,

  // Imperative props handed down from PinRepConfiguration
  showEditButton: boolean,
  showOverflowMenu: boolean,
  showShareButton: boolean,
  sourceLinkStyle: SourceLinkStyle,
  useDenseGroupBoardLayout: boolean,
|};

// The ActionBar is the bottom row of buttons on the PinRep, representing
// the secondary actions the user can take on the PinRep.
function ActionBar({
  actionBarEditBeforeShare,
  feedbackData,
  feedbackType,
  onToggleFlyout,
  onToggleModal,
  pin,
  pinTitle,
  setForcePrimary,
  showEditButton,
  showOverflowMenu,
  showShareButton,
  sourceLinkStyle,
  useDenseGroupBoardLayout,
}: Props) {
  const { viewParameter, viewType } = useTrackingParameters();
  const { carousel_data: carouselData, id: pinId, tracked_link: trackedLink, link } = pin;
  const sourceLinkURL = trackedLink || link || '';
  const showSourceLinkButton = !!sourceLinkURL;

  const sourceLinkButton =
    showSourceLinkButton &&
    enablePointerEvents(
      <SourceLink pin={pin} url={sourceLinkURL} sourceLinkStyle={sourceLinkStyle} />,
    );

  const sendShareLinkButton = showShareButton && (
    <SafeSuspense>
      <Box data-test-id="send-share-link">
        {enablePointerEvents(<SendShareLink pin={pin} setForcePrimary={setForcePrimary} />)}
      </Box>
    </SafeSuspense>
  );

  const editButton = showEditButton && (
    <SafeSuspense>
      <Box data-test-id="edit-button">
        {enablePointerEvents(
          <EditButton
            carouselSlotCurrentIndex={carouselData && carouselData.index}
            component={PCONST.thrift.ComponentType.FLOWED_PIN}
            onToggleModal={onToggleFlyout}
            pinId={pinId}
            style={{ size: 'sm' }}
            view={viewType}
            viewParameter={viewParameter}
            useLegoStyles
          />,
        )}
      </Box>
    </SafeSuspense>
  );

  // Group board dense view has a slightly different, evenly-spaced layout
  if (useDenseGroupBoardLayout) {
    return (
      <Box display="flex" justifyContent="between" alignItems="center" padding={3}>
        <div style={{ flexBasis: 32 }}>{sourceLinkButton}</div>
        {actionBarEditBeforeShare ? editButton : sendShareLinkButton}
        {actionBarEditBeforeShare ? sendShareLinkButton : editButton}
      </Box>
    );
  }

  return (
    <Box alignItems="center" display="flex" padding={3}>
      <Box flex="shrink">{sourceLinkButton}</Box>

      <Box
        alignItems="center"
        display="flex"
        flex="none"
        dangerouslySetInlineStyle={{ __style: { marginLeft: 'auto' } }}
      >
        <Box
          paddingX={
            showOverflowMenu || (actionBarEditBeforeShare ? showShareButton : showEditButton)
              ? 2
              : 0
          }
        >
          {actionBarEditBeforeShare ? editButton : sendShareLinkButton}
        </Box>

        {/* Only one of these should ever show */}
        {showOverflowMenu && (
          <SafeSuspense>
            <Box data-test-id="overflow-menu-button">
              {enablePointerEvents(
                <OverflowMenu
                  feedbackData={feedbackData}
                  feedbackType={feedbackType}
                  pin={pin}
                  pinTitle={pinTitle}
                  setForcePrimary={setForcePrimary}
                />,
              )}
            </Box>
          </SafeSuspense>
        )}
        {actionBarEditBeforeShare ? sendShareLinkButton : editButton}
      </Box>
    </Box>
  );
}

export default memo<Props>(ActionBar);
